import { Injectable } from "@angular/core";
import {
  HttpClient,
  HttpParams,
  HttpResponse,
} from "@angular/common/http";
import { Observable } from "rxjs";

import {
  SupportTicket_create,
  SupportTicket_update,
  SupportTicket_delete,
  SupportTicket_readAll,
  SupportTicket_readById,
  SupportTicket_readByTicketOwner,
  SupportTicket_readAllPaginated,
  SupportTicket_forceClose,
  SupportTicket_reOpen,
  SupportTicket_takeOver,
  SupportTicket_getAdminDashboard,
  SupportTicket_uploadAttachment,
  SupportTicket_viewAttachment,

  TicketActivity_create,
  TicketActivity_update,
  TicketActivity_delete,
  TicketActivity_readAll,
  TicketActivity_readById,
  TicketActivity_readByTicketId,

  TicketAssignee_create,
  TicketAssignee_update,
  TicketAssignee_delete,
  TicketAssignee_readAll,
  TicketAssignee_readById,
  TicketAssignee_readByTicketId,
  TicketAssignee_updateStatus,
} from "src/app/GlobalVariable";

import {
  SupportTicketAttachmentUploadResponseDTO,
  SupportTicketsDTO,
  SupportTicketsList,
  SupportTicketsCreateRequestDTO,
  SupportTicketsUpdateRequestDTO,
  SupportTicketsDeleteRequestDTO,
  SupportTicketsReadByIdRequestDTO,
  SupportTicketsReadByTicketOwnerRequestDTO,
  SupportTicketsReadAllPaginatedRequestDTO,
  SupportTicketsReadAllPaginatedList,
  SupportTicketsForceCloseRequestDTO,
  SupportTicketsReOpenRequestDTO,
  SupportTicketsTakeOverRequestDTO,
  SupportTicketsGetAdminDashboardRequestDTO,
  SupportTicketsDashboardDTO,

  TicketActivityDTO,
  TicketActivityList,
  TicketActivityCreateRequestDTO,
  TicketActivityUpdateRequestDTO,
  TicketActivityDeleteRequestDTO,
  TicketActivityReadByIdRequestDTO,
  TicketActivityReadByTicketIdRequestDTO,

  TicketAssigneeDTO,
  TicketAssigneeList,
  TicketAssigneeCreateRequestDTO,
  TicketAssigneeUpdateRequestDTO,
  TicketAssigneeDeleteRequestDTO,
  TicketAssigneeReadByIdRequestDTO,
  TicketAssigneeReadByTicketIdRequestDTO,
  TicketAssigneeUpdateStatusRequestDTO,
} from "./raise-ticket.interface";

@Injectable({
  providedIn: "root",
})
export class RaiseTicketService {
  constructor(
    private http: HttpClient,
  ) {}

  SupportTickets_Create(
    data: SupportTicketsCreateRequestDTO,
  ): Observable<SupportTicketsDTO> {
    return this.http.post<SupportTicketsDTO>(
      SupportTicket_create,
      data,
    );
  }

  /*
   * Backend DTO:
   *
   * public long TicketId { get; set; }
   * public IFormFile Attachment { get; set; }
   * public string ActionUser { get; set; }
   *
   * FormData field names must match those DTO properties.
   */
  SupportTicket_UploadAttachment(
    ticketId: number,
    file: File,
    actionUser: string,
  ): Observable<SupportTicketAttachmentUploadResponseDTO> {
    const formData = new FormData();

    formData.append(
      "TicketId",
      String(ticketId),
    );

    formData.append(
      "Attachment",
      file,
      file.name,
    );

    formData.append(
      "ActionUser",
      String(actionUser),
    );

    return this.http.post<SupportTicketAttachmentUploadResponseDTO>(
      SupportTicket_uploadAttachment,
      formData,
      {
        /*
         * Do not manually set Content-Type.
         * The browser must add the multipart boundary.
         */
        headers: {
          "Skip-Encryption": "true",
        },
      },
    );
  }

  /*
   * Loads one exact attachment through the authenticated gateway endpoint.
   *
   * Final request:
   * GET /M/ST/ViewAttachment/{ticketId}
   *     ?attachmentPath=Uploads/SupportTickets/{ticketId}/{fileName}
   *
   * HttpParams performs query-string encoding. Do not encode the complete
   * path manually before passing it to HttpParams.
   */
  SupportTicket_ViewAttachment(
    ticketId: number,
    attachmentPath?: string | null,
  ): Observable<HttpResponse<Blob>> {
    const validTicketId = Number(ticketId);

    if (
      !Number.isFinite(validTicketId) ||
      validTicketId <= 0
    ) {
      throw new Error(
        "Valid TicketId is required to view an attachment.",
      );
    }

    const normalizedPath =
      this.normalizeAttachmentPath(
        attachmentPath,
      );

    let params = new HttpParams();

    if (normalizedPath) {
      params = params.set(
        "attachmentPath",
        normalizedPath,
      );
    }

    const requestUrl =
      `${SupportTicket_viewAttachment}/${encodeURIComponent(
        String(validTicketId),
      )}`;

    return this.http.get(
      requestUrl,
      {
        params,
        observe: "response",
        responseType: "blob",
        headers: {
          /*
           * The attachment response is binary and must not be decrypted as
           * the normal encrypted JSON wrapper.
           */
          "Skip-Encryption": "true",
          Accept:
            "application/pdf,image/jpeg,image/png,application/octet-stream,*/*",
        },
      },
    );
  }

  /*
   * Keeps only the relative stored path expected by the backend.
   *
   * Supported values:
   * Uploads/SupportTickets/41/file.png
   * /Uploads/SupportTickets/41/file.png
   * https://host/Uploads/SupportTickets/41/file.png
   */
  private normalizeAttachmentPath(
    attachmentPath?: string | null,
  ): string {
    const value =
      String(
        attachmentPath || "",
      )
        .trim()
        .replace(
          /\\/g,
          "/",
        );

    if (!value) {
      return "";
    }

    try {
      if (/^https?:\/\//i.test(value)) {
        return decodeURIComponent(
          new URL(value).pathname,
        )
          .replace(/^\/+/, "")
          .split("?")[0]
          .split("#")[0];
      }
    } catch {
      /*
       * Use the value as a relative path when it is not a valid URL.
       */
    }

    return value
      .replace(/^\/+/, "")
      .split("?")[0]
      .split("#")[0];
  }

  SupportTickets_Update(
    data: SupportTicketsUpdateRequestDTO,
  ): Observable<SupportTicketsDTO> {
    return this.http.post<SupportTicketsDTO>(
      SupportTicket_update,
      data,
    );
  }

  SupportTickets_Delete(
    data: SupportTicketsDeleteRequestDTO,
  ): Observable<any> {
    return this.http.post<any>(
      SupportTicket_delete,
      data,
    );
  }

  SupportTickets_ReadAll(): Observable<SupportTicketsList> {
    return this.http.post<SupportTicketsList>(
      SupportTicket_readAll,
      {},
    );
  }

  SupportTickets_ReadById(
    data: SupportTicketsReadByIdRequestDTO,
  ): Observable<SupportTicketsDTO> {
    return this.http.post<SupportTicketsDTO>(
      SupportTicket_readById,
      data,
    );
  }

  SupportTickets_ReadByTicketOwner(
    data: SupportTicketsReadByTicketOwnerRequestDTO,
  ): Observable<SupportTicketsList> {
    return this.http.post<SupportTicketsList>(
      SupportTicket_readByTicketOwner,
      data,
    );
  }

  SupportTickets_ReadAllPaginated(
    data: SupportTicketsReadAllPaginatedRequestDTO,
  ): Observable<SupportTicketsReadAllPaginatedList> {
    return this.http.post<SupportTicketsReadAllPaginatedList>(
      SupportTicket_readAllPaginated,
      data,
    );
  }

  SupportTickets_ForceClose(
    data: SupportTicketsForceCloseRequestDTO,
  ): Observable<any> {
    return this.http.post<any>(
      SupportTicket_forceClose,
      data,
    );
  }

  SupportTickets_ReOpen(
    data: SupportTicketsReOpenRequestDTO,
  ): Observable<any> {
    return this.http.post<any>(
      SupportTicket_reOpen,
      data,
    );
  }

  SupportTickets_TakeOver(
    data: SupportTicketsTakeOverRequestDTO,
  ): Observable<any> {
    return this.http.post<any>(
      SupportTicket_takeOver,
      data,
    );
  }

  SupportTickets_GetAdminDashboard(
    data: SupportTicketsGetAdminDashboardRequestDTO,
  ): Observable<SupportTicketsDashboardDTO> {
    return this.http.post<SupportTicketsDashboardDTO>(
      SupportTicket_getAdminDashboard,
      data,
    );
  }

  TicketActivity_Create(
    data: TicketActivityCreateRequestDTO,
  ): Observable<TicketActivityDTO> {
    return this.http.post<TicketActivityDTO>(
      TicketActivity_create,
      data,
    );
  }

  TicketActivity_Update(
    data: TicketActivityUpdateRequestDTO,
  ): Observable<TicketActivityDTO> {
    return this.http.post<TicketActivityDTO>(
      TicketActivity_update,
      data,
    );
  }

  TicketActivity_Delete(
    data: TicketActivityDeleteRequestDTO,
  ): Observable<number> {
    return this.http.post<number>(
      TicketActivity_delete,
      data,
    );
  }

  TicketActivity_ReadAll(): Observable<TicketActivityList> {
    return this.http.post<TicketActivityList>(
      TicketActivity_readAll,
      {},
    );
  }

  TicketActivity_ReadById(
    data: TicketActivityReadByIdRequestDTO,
  ): Observable<TicketActivityDTO> {
    return this.http.post<TicketActivityDTO>(
      TicketActivity_readById,
      data,
    );
  }

  TicketActivity_ReadByTicketId(
    data: TicketActivityReadByTicketIdRequestDTO,
  ): Observable<TicketActivityList> {
    return this.http.post<TicketActivityList>(
      TicketActivity_readByTicketId,
      data,
    );
  }

  TicketAssignee_Create(
    data: TicketAssigneeCreateRequestDTO,
  ): Observable<TicketAssigneeDTO> {
    return this.http.post<TicketAssigneeDTO>(
      TicketAssignee_create,
      data,
    );
  }

  TicketAssignee_Update(
    data: TicketAssigneeUpdateRequestDTO,
  ): Observable<TicketAssigneeDTO> {
    return this.http.post<TicketAssigneeDTO>(
      TicketAssignee_update,
      data,
    );
  }

  TicketAssignee_Delete(
    data: TicketAssigneeDeleteRequestDTO,
  ): Observable<number> {
    return this.http.post<number>(
      TicketAssignee_delete,
      data,
    );
  }

  TicketAssignee_ReadAll(): Observable<TicketAssigneeList> {
    return this.http.post<TicketAssigneeList>(
      TicketAssignee_readAll,
      {},
    );
  }

  TicketAssignee_ReadById(
    data: TicketAssigneeReadByIdRequestDTO,
  ): Observable<TicketAssigneeDTO> {
    return this.http.post<TicketAssigneeDTO>(
      TicketAssignee_readById,
      data,
    );
  }

  TicketAssignee_ReadByTicketId(
    data: TicketAssigneeReadByTicketIdRequestDTO,
  ): Observable<TicketAssigneeList> {
    return this.http.post<TicketAssigneeList>(
      TicketAssignee_readByTicketId,
      data,
    );
  }

  TicketAssignee_UpdateStatus(
    data: TicketAssigneeUpdateStatusRequestDTO,
  ): Observable<TicketAssigneeDTO> {
    return this.http.post<TicketAssigneeDTO>(
      TicketAssignee_updateStatus,
      data,
    );
  }
}
